import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Avatar, Chip,
  IconButton, CircularProgress, Alert, Divider, Tooltip,
  LinearProgress, ToggleButton, ToggleButtonGroup, Skeleton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  School as StudentsIcon,
  HowToReg as AdmissionsIcon,
  Badge as StaffIcon,
  MeetingRoom as ClassIcon,
  FactCheck as RegisterIcon,
  Cake as CakeIcon,
  Insights as TrendIcon,
  Groups as GroupsIcon,
  Refresh as RefreshIcon,
  EmojiEvents as AlumniIcon,
  WbSunny as MorningIcon,
  Elderly as AgeIcon,
  AssignmentInd as DutyIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import API from '../../BaseURL';

// ─────────────────────────────────────────────────────────────────
// THE HOME PAGE
//
// Every staff account lands here. It is built around the school —
// how many learners there are, how the roll has moved over the years,
// who is in class today, whose birthday it is — because that is the
// one story every single person signing in has a stake in, from the
// headteacher to the registrar to the exams officer.
//
// Money is not the school's story, and most accounts have no business
// reading a cash position, so no figure on this page is a cedi figure.
// Finance lives on the Cashbook, Petty and Ledger pages, which is where
// anyone looking for it goes.
//
// NO WAY OUT — this page navigates NOWHERE. Do not add a link, an
// arrow, an "Admit" button or a clickable tile to it.
//
// Everything here is an aggregate that is safe for any staff account to
// read. The pages behind it are not: Students, Admissions, Admission
// Details, Classes, Completion and Staff are account-specific and
// access-levelled, and a fee record or an admission form is not
// something a landing page may hand out. A shortcut from a screen
// EVERYONE lands on into a screen NOT everyone may open is a hole,
// whatever the route guard does afterwards — the guard is the last
// line, not a licence to aim people at doors they will be refused at.
//
// So the panels are read-only surfaces. Navigation belongs in the
// sidebar, which is already built from the signed-in user's own page
// permissions and only ever shows them what they hold.
//
// Charts are hand-rolled SVG on purpose: the app ships no charting
// library, and adding one for six figures would cost more than it
// returns.
// ─────────────────────────────────────────────────────────────────

const TNUM = { fontVariantNumeric: 'tabular-nums' };

const fmt0 = (n) => Number(n || 0).toLocaleString();

const initials = (name) => String(name || '')
  .trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';

// Chart palette. Deliberately one step more saturated than the MUI
// defaults — these are read at a glance from across a desk.
const C = {
  blue:   '#1565C0',
  sky:    '#0EA5E9',
  male:   '#2563EB',
  female: '#DB2777',
  green:  '#16A34A',
  red:    '#DC2626',
  amber:  '#F59E0B',
  violet: '#7C3AED',
  teal:   '#0D9488',
  slate:  '#64748B',
  grid:   '#E6EBF2',
};

const isSuper = (u) => u?.role === 'super_admin';

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// Measure the container so charts can size their text in real pixels.
// A fixed viewBox scaled by the browser would shrink axis labels to
// nothing on a phone, which is exactly where legibility matters most.
function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setW(el.clientWidth || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

// ── Shared shell ────────────────────────────────────────────────
function Panel({ icon, title, subtitle, accent = C.blue, action, children, sx }) {
  return (
    <Card sx={{
      borderRadius: 3, height: '100%',
      display: 'flex', flexDirection: 'column',
      border: '1px solid', borderColor: 'divider',
      boxShadow: '0 1px 3px rgba(16,24,40,.06), 0 8px 24px -12px rgba(16,24,40,.18)',
      overflow: 'hidden',
      ...sx,
    }}>
      <Box sx={{
        px: 2, py: 1.4, display: 'flex', alignItems: 'center', gap: 1.3,
        borderBottom: '1px solid', borderColor: 'divider',
        background: `linear-gradient(90deg, ${alpha(accent, 0.07)}, transparent 60%)`,
      }}>
        <Avatar variant="rounded" sx={{
          width: 32, height: 32, borderRadius: 2,
          bgcolor: alpha(accent, 0.14), color: accent,
        }}>
          {icon}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.3 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, flex: 1 }}>
        {children}
      </CardContent>
    </Card>
  );
}

function Empty({ children }) {
  return (
    <Typography variant="caption" color="text.disabled"
      sx={{ fontStyle: 'italic', textAlign: 'center', display: 'block', py: 3 }}>
      {children}
    </Typography>
  );
}

// ── Reusable donut ──────────────────────────────────────────────
function Donut({ segments = [], size = 132, thickness = 16, children }) {
  const total = segments.reduce((s, x) => s + Number(x.value || 0), 0) || 1;
  const r = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;
  let acc = 0;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={C.grid} strokeWidth={thickness} />
        {segments.map((s, i) => {
          const len = (Number(s.value || 0) / total) * circ;
          const node = (
            <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none"
              stroke={s.color} strokeWidth={thickness}
              strokeDasharray={`${len} ${circ - len}`}
              strokeDashoffset={-acc}
              style={{ transition: 'stroke-dasharray .5s ease' }} />
          );
          acc += len;
          return node;
        })}
      </svg>
      <Box sx={{
        position: 'absolute', inset: 0, display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        {children}
      </Box>
    </Box>
  );
}

function LegendDot({ color, label, value, pct }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 0 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 800, ...TNUM }}>{fmt0(value)}</Typography>
      {pct != null && (
        <Typography sx={{ fontSize: 10.5, color: 'text.secondary', ...TNUM, width: 40, textAlign: 'right' }}>
          {pct.toFixed(1)}%
        </Typography>
      )}
    </Stack>
  );
}

// ── The trajectory chart ────────────────────────────────────────
//
// Two stacked regions sharing one x axis, the way a price chart sits
// above its volume bars:
//   top     the roll — how many learners the school actually held,
//           as a filled area, so the shape of the school's growth is
//           the first thing the eye lands on
//   bottom  intake and departures for that same bucket, as paired
//           columns, so a flat roll with heavy churn underneath
//           doesn't read as "nothing happened"
//
// `showRoll` is off for the monthly view: a rolling total across
// eighteen months is dominated by whichever month term started, and
// the bars alone tell that story honestly.
function TrajectoryChart({ data = [], showRoll = true, height = 300 }) {
  const [ref, W] = useWidth();
  const [hover, setHover] = useState(null);

  if (!data.length) return <Box ref={ref}><Empty>No admission dates recorded yet.</Empty></Box>;
  if (!W) return <Box ref={ref} sx={{ height }} />;

  const padL = 44, padR = 14, padT = 14, padB = 28;
  const plotW = Math.max(W - padL - padR, 10);
  const inner = height - padT - padB;
  const rollH = showRoll ? Math.round(inner * 0.60) : 0;
  const gap   = showRoll ? 16 : 0;
  const barH  = inner - rollH - gap;
  const barTop = padT + rollH + gap;

  const band = plotW / data.length;
  const cx = (i) => padL + band * (i + 0.5);

  const rollMax = Math.max(...data.map(d => Number(d.roll || 0)), 1) * 1.12;
  const flowMax = Math.max(...data.map(d => Math.max(d.joined || 0, d.left || 0)), 1) * 1.15;

  const rollY = (v) => padT + rollH - (Number(v || 0) / rollMax) * rollH;
  const flowH = (v) => (Number(v || 0) / flowMax) * barH;

  // Area path for the roll, closed along the baseline.
  const pts = data.map((d, i) => `${cx(i)},${rollY(d.roll)}`);
  const line = `M ${pts.join(' L ')}`;
  const area = `${line} L ${cx(data.length - 1)},${padT + rollH} L ${cx(0)},${padT + rollH} Z`;

  const barW = Math.max(Math.min(band * 0.30, 15), 3);
  // Thin the x labels out until they stop colliding. The threshold has
  // to come from the widest label actually present, not a constant:
  // "2019" and "Mar 25" need very different amounts of room, and the
  // panel is narrow enough now that guessing wrong overlaps them.
  const widest = Math.max(...data.map(d => String(d.label || '').length), 1);
  const needed = widest * 6.4 + 10;
  const labelStep = band < needed ? Math.ceil(needed / band) : 1;

  const h = hover != null ? data[hover] : null;

  return (
    <Box ref={ref} sx={{ position: 'relative' }}>
      <svg width={W} height={height} style={{ display: 'block', overflow: 'visible' }}>
        {/* roll gridlines + axis */}
        {showRoll && [0, 0.5, 1].map((f, i) => {
          const y = padT + rollH - f * rollH;
          const v = Math.round(rollMax * f);
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={W - padR} y2={y}
                stroke={C.grid} strokeWidth="1" strokeDasharray={f === 0 ? '' : '3 4'} />
              <text x={padL - 8} y={y + 4} textAnchor="end"
                fontSize="10" fill={C.slate} fontWeight="600">{fmt0(v)}</text>
            </g>
          );
        })}

        {showRoll && (
          <>
            <defs>
              <linearGradient id="rollFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={C.blue} stopOpacity="0.30" />
                <stop offset="100%" stopColor={C.blue} stopOpacity="0.02" />
              </linearGradient>
            </defs>
            <path d={area} fill="url(#rollFill)" />
            <path d={line} fill="none" stroke={C.blue} strokeWidth="2.5"
              strokeLinejoin="round" strokeLinecap="round" />
            {data.map((d, i) => (
              <circle key={i} cx={cx(i)} cy={rollY(d.roll)} r={hover === i ? 5 : 3}
                fill="#fff" stroke={C.blue} strokeWidth="2" />
            ))}
          </>
        )}

        {/* intake / departure columns */}
        <line x1={padL} y1={barTop + barH} x2={W - padR} y2={barTop + barH}
          stroke={C.grid} strokeWidth="1" />
        {data.map((d, i) => {
          const jH = flowH(d.joined), lH = flowH(d.left);
          const x  = cx(i);
          return (
            <g key={i}>
              <rect x={x - barW - 1.5} y={barTop + barH - jH} width={barW} height={jH}
                rx={Math.min(barW / 2, 3)} fill={C.sky}
                opacity={hover == null || hover === i ? 1 : 0.45} />
              <rect x={x + 1.5} y={barTop + barH - lH} width={barW} height={lH}
                rx={Math.min(barW / 2, 3)} fill={C.amber}
                opacity={hover == null || hover === i ? 1 : 0.45} />
            </g>
          );
        })}

        {/* x labels */}
        {data.map((d, i) => (
          (i % labelStep === 0 || i === data.length - 1) ? (
            <text key={i} x={cx(i)} y={height - 8} textAnchor="middle"
              fontSize="10.5" fontWeight={hover === i ? 800 : 600}
              fill={hover === i ? C.blue : C.slate}>
              {d.label}
            </text>
          ) : null
        ))}

        {/* hover targets — one invisible column per bucket */}
        {data.map((d, i) => (
          <rect key={i} x={padL + band * i} y={padT} width={band} height={inner}
            fill="transparent" style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)} />
        ))}
        {hover != null && (
          <line x1={cx(hover)} y1={padT} x2={cx(hover)} y2={padT + inner}
            stroke={C.blue} strokeWidth="1" strokeDasharray="3 3" opacity={0.5} />
        )}
      </svg>

      {h && (
        <Box sx={{
          position: 'absolute', top: 0, pointerEvents: 'none',
          left: Math.min(Math.max(cx(hover) - 70, 0), Math.max(W - 145, 0)),
          width: 145, p: 1.1, borderRadius: 2,
          bgcolor: 'rgba(15,23,42,.94)', color: '#fff',
          boxShadow: '0 8px 24px rgba(0,0,0,.25)',
        }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 800, mb: 0.4 }}>{h.label}</Typography>
          {showRoll && (
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: 11, opacity: .8 }}>On roll</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 800, ...TNUM }}>{fmt0(h.roll)}</Typography>
            </Stack>
          )}
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontSize: 11, color: C.sky }}>Admitted</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 800, ...TNUM }}>+{fmt0(h.joined)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontSize: 11, color: C.amber }}>Left</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 800, ...TNUM }}>−{fmt0(h.left)}</Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// ── Age histogram ───────────────────────────────────────────────
function AgeChart({ data = [], height = 150 }) {
  const [ref, W] = useWidth();
  if (!data.length) return <Box ref={ref}><Empty>No dates of birth recorded.</Empty></Box>;
  if (!W) return <Box ref={ref} sx={{ height }} />;

  const padB = 22, padT = 16;
  const plotH = height - padB - padT;
  const band = W / data.length;
  const max = Math.max(...data.map(d => d.n), 1);
  const barW = Math.max(Math.min(band * 0.66, 30), 4);
  const labelStep = band < 24 ? Math.ceil(24 / band) : 1;

  return (
    <Box ref={ref}>
      <svg width={W} height={height} style={{ display: 'block' }}>
        {data.map((d, i) => {
          const hM = (d.male   / max) * plotH;
          const hF = (d.female / max) * plotH;
          const x = band * i + (band - barW) / 2;
          const yF = padT + plotH - hF;
          const yM = yF - hM;
          return (
            <g key={i}>
              {/* Native SVG title — a real browser tooltip, and one less
                  portal than wrapping every column in a MUI Tooltip. */}
              <title>{`Age ${d.age}: ${d.n} (${d.male} boys, ${d.female} girls)`}</title>
              <rect x={x} y={yF} width={barW} height={hF} fill={C.female} />
              <rect x={x} y={yM} width={barW} height={hM} fill={C.male}
                rx={Math.min(barW / 2, 3)} />
              {(i % labelStep === 0) && (
                <text x={band * i + band / 2} y={height - 7} textAnchor="middle"
                  fontSize="10" fill={C.slate} fontWeight="600">{d.age}</text>
              )}
            </g>
          );
        })}
        <line x1={0} y1={padT + plotH} x2={W} y2={padT + plotH} stroke={C.grid} strokeWidth="1" />
      </svg>
      <Typography variant="caption" color="text.secondary"
        sx={{ display: 'block', textAlign: 'center', fontSize: 10.5, mt: 0.3 }}>
        Age in years
      </Typography>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════
// PANELS
// ═══════════════════════════════════════════════════════════════

// ── Welcome band ────────────────────────────────────────────────
function WelcomeBand({ user, me, period, school, loading, onRefresh }) {
  const now = new Date();
  const roleLabel = me?.role_label || user?.role_label
    || (isSuper(user) ? 'Super Administrator' : (user?.role || 'User'));

  return (
    <Card sx={{
      borderRadius: 3, mb: 2.5, color: '#fff', position: 'relative', overflow: 'hidden',
      background: 'linear-gradient(120deg, #0B3C91 0%, #1565C0 48%, #2196F3 100%)',
      boxShadow: '0 10px 30px -12px rgba(13,71,161,.55)',
    }}>
      {/* Soft light bloom — keeps the band from reading as a flat slab. */}
      <Box sx={{
        position: 'absolute', top: -120, right: -80, width: 340, height: 340,
        borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,.20), transparent 65%)',
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: -140, left: '35%', width: 300, height: 300,
        borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,.12), transparent 65%)',
        pointerEvents: 'none',
      }} />

      <CardContent sx={{ p: { xs: 2, sm: 2.8 }, position: 'relative' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ md: 'center' }}>
          {/* Who you are */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Avatar sx={{
              width: 62, height: 62, fontSize: 23, fontWeight: 800,
              bgcolor: 'rgba(255,255,255,.18)', color: '#fff',
              border: '2px solid rgba(255,255,255,.45)',
            }}>
              {initials(user?.full_name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12.5, opacity: .85, fontWeight: 600, letterSpacing: .3 }}>
                <MorningIcon sx={{ fontSize: 14, mb: '-2px', mr: .5 }} />
                {greeting()}
              </Typography>
              <Typography sx={{ fontSize: { xs: 22, sm: 27 }, fontWeight: 800, lineHeight: 1.15 }}>
                {user?.full_name?.split(' ')[0] || 'there'}
              </Typography>
              <Stack direction="row" spacing={0.7} flexWrap="wrap" rowGap={0.7} sx={{ mt: 0.7 }}>
                <Chip size="small" label={roleLabel}
                  sx={{ bgcolor: 'rgba(255,255,255,.20)', color: '#fff', fontWeight: 700, fontSize: 11 }} />
                {me?.department && (
                  <Chip size="small" label={me.department}
                    sx={{ bgcolor: 'rgba(255,255,255,.12)', color: '#fff', fontWeight: 600, fontSize: 11 }} />
                )}
                {me?.module_count != null && (
                  <Chip size="small" label={`${me.module_count} module${me.module_count === 1 ? '' : 's'}`}
                    sx={{ bgcolor: 'rgba(255,255,255,.12)', color: '#fff', fontWeight: 600, fontSize: 11 }} />
                )}
              </Stack>
            </Box>
          </Stack>

          {/* Where the school is right now */}
          <Box sx={{
            flex: 1, minWidth: 0,
            borderLeft: { md: '1px solid rgba(255,255,255,.22)' },
            borderTop: { xs: '1px solid rgba(255,255,255,.22)', md: 'none' },
            pl: { md: 2.5 }, pt: { xs: 1.8, md: 0 },
          }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.25 }} noWrap>
              {school || 'Your school'}
            </Typography>
            <Typography sx={{ fontSize: 12.5, opacity: .88 }}>
              {now.toLocaleDateString(undefined,
                { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </Typography>

            {period?.term ? (
              <Box sx={{ mt: 1.2 }}>
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: .3 }}>
                    {String(period.term).replace(/_/g, ' ')} · {period.acc_yer}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {period.total_days > 0 && (
                    <Typography sx={{ fontSize: 11, opacity: .9, ...TNUM }}>
                      {period.ended
                        ? 'Term ended'
                        : period.not_started
                          ? 'Not yet started'
                          : `${period.remaining_days} day${period.remaining_days === 1 ? '' : 's'} left`}
                    </Typography>
                  )}
                </Stack>
                {period.total_days > 0 && (
                  <>
                    <LinearProgress variant="determinate" value={Math.min(period.pct, 100)}
                      sx={{
                        mt: .6, height: 7, borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,.22)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: period.ended ? '#FCD34D' : '#7CE2A8', borderRadius: 4,
                        },
                      }} />
                    <Typography sx={{ fontSize: 10.5, opacity: .8, mt: .4, ...TNUM }}>
                      {period.ended
                        ? `Ran ${period.start} → ${period.end} · roll over to the next term when ready`
                        : `Day ${period.elapsed_days} of ${period.total_days} · ${period.pct}% through the term`}
                    </Typography>
                  </>
                )}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 11.5, opacity: .8, mt: 1 }}>
                No active academic term set.
              </Typography>
            )}

            {me?.my_classes?.length > 0 && (
              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 1.2 }}>
                <DutyIcon sx={{ fontSize: 15, opacity: .9 }} />
                <Typography sx={{ fontSize: 11.5, opacity: .92 }}>
                  Register duty: {me.my_classes.join(', ')} ·{' '}
                  <b>{me.my_classes_marked_today}/{me.my_classes.length}</b> marked today
                </Typography>
              </Stack>
            )}
          </Box>

          <IconButton onClick={onRefresh} disabled={loading}
            sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', alignSelf: { xs: 'flex-end', md: 'center' },
                  '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}>
            {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Headline tiles ──────────────────────────────────────────────
// Read-only by design — see the NO WAY OUT note at the top of the file.
function Tile({ icon, label, value, sub, color }) {
  return (
    <Card sx={{
      p: 1.8, borderRadius: 3,
      border: '1px solid', borderColor: 'divider',
      position: 'relative', overflow: 'hidden',
      '&::before': {
        content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: color,
      },
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar variant="rounded" sx={{
          width: 46, height: 46, borderRadius: 2.5,
          bgcolor: alpha(color, .12), color,
        }}>
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontSize: 10, fontWeight: 800, letterSpacing: .6, textTransform: 'uppercase',
            color: 'text.secondary', display: 'block',
          }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 26, lineHeight: 1.1, ...TNUM }}>
            {value}
          </Typography>
          {sub && (
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>{sub}</Typography>
          )}
        </Box>
      </Stack>
    </Card>
  );
}

// ── Class distribution ──────────────────────────────────────────
function ClassPanel({ rows = [] }) {
  const max = Math.max(...rows.map(r => r.total), 1);
  return (
    <Panel
      icon={<ClassIcon fontSize="small" />}
      title="Learners by class"
      subtitle={`${rows.length} class${rows.length === 1 ? '' : 'es'} in session`}
      accent={C.violet}>
      {rows.length === 0 ? <Empty>No classes set up yet.</Empty> : (
        // Name and bar share a line so a fourteen-class school still
        // fits the analysis band without stretching it. The class
        // teacher moves into the row's tooltip rather than off the
        // page — it is reference detail, not something read at a
        // glance.
        <Stack spacing={0.45} sx={{ maxHeight: 356, overflowY: 'auto', pr: .5 }}>
          {rows.map((r, i) => {
            const pct  = (r.total / max) * 100;
            const mPct = r.total ? (r.male / r.total) * 100 : 0;
            const fPct = r.total ? (r.female / r.total) * 100 : 0;
            return (
              <Tooltip key={i} placement="left"
                title={`${r.class_name} — ${r.total} learners (${r.male} boys, ${r.female} girls)${r.teacher ? ` · ${r.teacher}` : ''}`}>
                <Stack direction="row" alignItems="center" spacing={1}
                  sx={{ cursor: 'default' }}>
                  <Typography sx={{
                    fontSize: 11.5, fontWeight: 700, width: 74, flexShrink: 0,
                  }} noWrap>
                    {r.class_name}
                  </Typography>
                  <Box sx={{
                    flex: 1, minWidth: 0, height: 12, borderRadius: 4,
                    bgcolor: C.grid, overflow: 'hidden', display: 'flex',
                  }}>
                    <Box sx={{ width: `${(pct * mPct) / 100}%`, bgcolor: C.male, transition: 'width .5s ease' }} />
                    <Box sx={{ width: `${(pct * fPct) / 100}%`, bgcolor: C.female, transition: 'width .5s ease' }} />
                  </Box>
                  <Typography sx={{
                    fontSize: 12, fontWeight: 800, ...TNUM, width: 26, textAlign: 'right', flexShrink: 0,
                  }}>
                    {r.total}
                  </Typography>
                </Stack>
              </Tooltip>
            );
          })}
        </Stack>
      )}
      {rows.length > 0 && (
        <Stack direction="row" spacing={1.6} sx={{ mt: 1.2 }} alignItems="center">
          <Stack direction="row" spacing={.6} alignItems="center">
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: C.male }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Boys</Typography>
          </Stack>
          <Stack direction="row" spacing={.6} alignItems="center">
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: C.female }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Girls</Typography>
          </Stack>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 10, color: 'text.secondary', fontStyle: 'italic' }}>
            hover a class for its teacher
          </Typography>
        </Stack>
      )}
    </Panel>
  );
}

// ── Who's in school (gender donut + age histogram) ──────────────
function ProfilePanel({ population, ages }) {
  const total = Number(population?.total || 0);
  const male = Number(population?.male || 0);
  const female = Number(population?.female || 0);
  const other = Math.max(total - male - female, 0);
  return (
    <Panel
      icon={<GroupsIcon fontSize="small" />}
      title="Who is in school"
      subtitle="Gender balance and age profile"
      accent={C.female}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Donut size={112} thickness={14} segments={[
          { value: male,   color: C.male },
          { value: female, color: C.female },
          { value: other,  color: C.slate },
        ]}>
          <Typography sx={{ fontSize: 21, fontWeight: 800, lineHeight: 1, ...TNUM }}>
            {fmt0(total)}
          </Typography>
          <Typography sx={{ fontSize: 9, color: 'text.secondary', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: .5 }}>
            learners
          </Typography>
        </Donut>
        <Stack spacing={0.9} sx={{ flex: 1, minWidth: 0 }}>
          <LegendDot color={C.male}   label="Boys"  value={male}
            pct={total ? (male / total) * 100 : 0} />
          <LegendDot color={C.female} label="Girls" value={female}
            pct={total ? (female / total) * 100 : 0} />
          {other > 0 && (
            <LegendDot color={C.slate} label="Unrecorded" value={other}
              pct={total ? (other / total) * 100 : 0} />
          )}
          <Divider />
          <Stack direction="row" spacing={1.6}>
            {[
              { k: 'Avg class', v: population?.avg_class_size || 0 },
              { k: 'Alumni',    v: fmt0(population?.alumni) },
              { k: 'Left',      v: fmt0(population?.leavers) },
            ].map((s, i) => (
              <Box key={i}>
                <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: 'text.secondary',
                  textTransform: 'uppercase', letterSpacing: .5 }}>{s.k}</Typography>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, ...TNUM }}>{s.v}</Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Stack>

      {ages?.length > 0 && (
        <>
          <Divider sx={{ my: 1.2 }} />
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .2 }}>
            <AgeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: .5,
              textTransform: 'uppercase', color: 'text.secondary', flex: 1 }}>
              Age profile
            </Typography>
            <Stack direction="row" spacing={1.2}>
              <Stack direction="row" spacing={.5} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: C.male }} />
                <Typography sx={{ fontSize: 10 }}>Boys</Typography>
              </Stack>
              <Stack direction="row" spacing={.5} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: C.female }} />
                <Typography sx={{ fontSize: 10 }}>Girls</Typography>
              </Stack>
            </Stack>
          </Stack>
          <AgeChart data={ages} height={132} />
        </>
      )}
    </Panel>
  );
}

// ── Attendance ──────────────────────────────────────────────────
function AttendancePanel({ data }) {
  if (!data) {
    return (
      <Panel icon={<RegisterIcon fontSize="small" />} title="Today's register"
        subtitle="Morning roll call" accent={C.green}>
        <Empty>The daily register is not set up on this school yet.</Empty>
      </Panel>
    );
  }
  const { present, absent, marked, rate, classes_marked, classes_pending, trend } = data;
  const done = classes_marked + classes_pending > 0
    ? (classes_marked / (classes_marked + classes_pending)) * 100 : 0;
  const trendMax = Math.max(...(trend || []).map(t => t.present + t.absent), 1);

  return (
    <Panel
      icon={<RegisterIcon fontSize="small" />}
      title="Today's register"
      subtitle={marked > 0
        ? `${fmt0(marked)} learner${marked === 1 ? '' : 's'} accounted for`
        : 'Not yet taken'}
      accent={C.green}
      action={
        <Chip size="small"
          label={`${classes_marked}/${classes_marked + classes_pending} classes`}
          sx={{
            fontWeight: 800, fontSize: 11.5, height: 24,
            bgcolor: alpha(done >= 100 ? C.green : C.amber, .14),
            color: done >= 100 ? C.green : '#B45309',
          }} />
      }>
      {marked === 0 ? (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <RegisterIcon sx={{ fontSize: 34, color: 'text.disabled' }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700, mt: .5 }}>
            No register marked yet today
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {classes_pending} class{classes_pending === 1 ? '' : 'es'} waiting for roll call.
          </Typography>
        </Box>
      ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems="center">
          <Donut size={118} thickness={14} segments={[
            { value: present, color: C.green },
            { value: absent,  color: C.red },
          ]}>
            <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1, ...TNUM, color: C.green }}>
              {rate}%
            </Typography>
            <Typography sx={{ fontSize: 9.5, color: 'text.secondary', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: .5 }}>
              present
            </Typography>
          </Donut>
          <Stack spacing={1} sx={{ flex: 1, width: '100%' }}>
            <LegendDot color={C.green} label="Present" value={present} />
            <Typography sx={{ fontSize: 10.5, color: 'text.secondary', pl: 2.2, mt: '-6px !important' }}>
              {data.present_m} boys · {data.present_f} girls
            </Typography>
            <LegendDot color={C.red} label="Absent" value={absent} />
            <Typography sx={{ fontSize: 10.5, color: 'text.secondary', pl: 2.2, mt: '-6px !important' }}>
              {data.absent_m} boys · {data.absent_f} girls
            </Typography>
          </Stack>
        </Stack>
      )}

      {trend?.length > 1 && (
        <>
          <Divider sx={{ my: 1.6 }} />
          <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: .5,
            textTransform: 'uppercase', color: 'text.secondary', mb: .8 }}>
            Last {trend.length} school days
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="flex-end" sx={{ height: 54 }}>
            {trend.map((t, i) => {
              const h = ((t.present + t.absent) / trendMax) * 100;
              const pShare = (t.present + t.absent) ? (t.present / (t.present + t.absent)) * 100 : 0;
              return (
                <Tooltip key={i} title={`${t.date}: ${t.present} present, ${t.absent} absent (${t.rate.toFixed(0)}%)`}>
                  <Box sx={{
                    flex: 1, height: `${Math.max(h, 6)}%`, borderRadius: 1,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column-reverse',
                    bgcolor: C.grid, cursor: 'pointer',
                    transition: 'opacity .15s', '&:hover': { opacity: .8 },
                  }}>
                    <Box sx={{ height: `${pShare}%`, bgcolor: C.green }} />
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        </>
      )}

      {classes_pending > 0 && data.pending_classes?.length > 0 && (
        <>
          <Divider sx={{ my: 1.6 }} />
          <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: .5,
            textTransform: 'uppercase', color: 'text.secondary', mb: .7 }}>
            Still to be marked
          </Typography>
          <Stack direction="row" spacing={0.6} flexWrap="wrap" rowGap={0.6}>
            {data.pending_classes.map((c, i) => (
              <Chip key={i} size="small" label={c}
                sx={{ fontSize: 10.5, height: 22, fontWeight: 600,
                      bgcolor: alpha(C.amber, .12), color: '#B45309' }} />
            ))}
            {classes_pending > data.pending_classes.length && (
              <Chip size="small" label={`+${classes_pending - data.pending_classes.length} more`}
                sx={{ fontSize: 10.5, height: 22 }} />
            )}
          </Stack>
        </>
      )}
    </Panel>
  );
}

// ── Birthdays ───────────────────────────────────────────────────
function BirthdayPanel({ rows = [] }) {
  const todayCount = rows.filter(r => r.is_today).length;

  const Row = ({ r }) => (
    <Stack direction="row" alignItems="center" spacing={1.2} sx={{
      p: 1, borderRadius: 2,
      border: '1px solid', borderColor: r.is_today ? alpha(C.amber, .45) : 'divider',
      bgcolor: r.is_today ? alpha(C.amber, .09) : 'transparent',
    }}>
      <Avatar sx={{
        width: 30, height: 30, fontSize: 12, fontWeight: 800,
        bgcolor: alpha(r.gender === 'Male' ? C.male : C.female, .15),
        color: r.gender === 'Male' ? C.male : C.female,
      }}>
        {initials(r.name)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.2 }} noWrap>
          {r.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
          {r.class_name || 'Unassigned'} · turning {r.turning}
        </Typography>
      </Box>
      <Chip size="small"
        label={r.is_today ? 'Today' : r.days_away === 1 ? 'Tomorrow' : r.label}
        sx={{
          height: 21, fontSize: 10.5, fontWeight: 800,
          bgcolor: r.is_today ? C.amber : C.grid,
          color: r.is_today ? '#fff' : 'text.secondary',
        }} />
    </Stack>
  );

  return (
    <Panel
      icon={<CakeIcon fontSize="small" />}
      title="Birthdays"
      subtitle={todayCount > 0
        ? `${todayCount} learner${todayCount === 1 ? '' : 's'} celebrating today`
        : 'Coming up over the next 30 days'}
      accent={C.amber}
      action={<Chip size="small" label={`${rows.length}`} sx={{ fontWeight: 800, height: 24 }} />}>
      {rows.length === 0 ? <Empty>No birthdays in the next 30 days.</Empty> : (
        <Stack spacing={0.7} sx={{ maxHeight: 330, overflowY: 'auto', pr: .5 }}>
          {rows.slice(0, 10).map((r, i) => <Row key={i} r={r} />)}
        </Stack>
      )}
    </Panel>
  );
}

// ── Newest faces ────────────────────────────────────────────────
function NewFacesPanel({ rows = [] }) {
  return (
    <Panel
      icon={<AdmissionsIcon fontSize="small" />}
      title="Newest faces"
      subtitle="Most recently admitted learners"
      accent={C.teal}>
      {rows.length === 0 ? <Empty>No admissions recorded yet.</Empty> : (
        <Stack spacing={0.7}>
          {rows.map((s, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1.2}
              sx={{ p: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Avatar sx={{
                width: 32, height: 32, fontSize: 12, fontWeight: 800,
                bgcolor: alpha(s.gender === 'Male' ? C.male : C.female, .15),
                color: s.gender === 'Male' ? C.male : C.female,
              }}>
                {initials(s.name)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.2 }} noWrap>
                  {s.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
                  {s.index_no} · {s.class_name || 'Unassigned'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, ...TNUM }}>
                {s.date}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Panel>
  );
}

// ── Staff ───────────────────────────────────────────────────────
function StaffPanel({ data }) {
  if (!data || data.total === 0) {
    return (
      <Panel icon={<StaffIcon fontSize="small" />} title="Staff"
        subtitle="Teaching and non-teaching" accent={C.slate}>
        <Empty>No staff records captured yet.</Empty>
      </Panel>
    );
  }
  const maxDept = Math.max(...(data.departments || []).map(d => d.n), 1);
  return (
    <Panel
      icon={<StaffIcon fontSize="small" />}
      title="Staff"
      subtitle="Teaching and non-teaching"
      accent={C.slate}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Donut size={104} thickness={13} segments={[
          { value: data.teaching,     color: C.blue },
          { value: data.non_teaching, color: C.teal },
        ]}>
          <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1, ...TNUM }}>
            {fmt0(data.total)}
          </Typography>
          <Typography sx={{ fontSize: 9.5, color: 'text.secondary', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: .5 }}>staff</Typography>
        </Donut>
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <LegendDot color={C.blue} label="Teaching"     value={data.teaching} />
          <LegendDot color={C.teal} label="Non-teaching" value={data.non_teaching} />
          {(data.male > 0 || data.female > 0) && (
            <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>
              {data.male} men · {data.female} women
            </Typography>
          )}
        </Stack>
      </Stack>

      {data.departments?.length > 1 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: .5,
            textTransform: 'uppercase', color: 'text.secondary', mb: .8 }}>
            By department
          </Typography>
          <Stack spacing={0.8}>
            {data.departments.map((d, i) => (
              <Box key={i}>
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 600, flex: 1 }} noWrap>
                    {d.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 800, ...TNUM }}>{d.n}</Typography>
                </Stack>
                <Box sx={{ mt: .3, height: 5, borderRadius: 3, bgcolor: C.grid, overflow: 'hidden' }}>
                  <Box sx={{ width: `${(d.n / maxDept) * 100}%`, height: '100%', bgcolor: C.slate }} />
                </Box>
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Panel>
  );
}

// ═══════════════════════════════════════════════════════════════
// PAGE
// ═══════════════════════════════════════════════════════════════
export default function DashboardPage() {
  const { user, schoolName, currentSchool } = useAuth();
  const [snap, setSnap]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [span, setSpan]       = useState('year');   // 'year' | 'month'

  const load = async () => {
    setLoading(true);
    try {
      const r = await API.get('/dashboard/snapshot');
      setSnap(r.data || {});
      setError('');
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load your dashboard');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const school = currentSchool?.name || schoolName || '';

  const trajectory = useMemo(
    () => (span === 'year' ? snap?.trajectory_year : snap?.trajectory_month) || [],
    [snap, span]
  );

  const pop = snap?.population;
  const reg = snap?.register;

  return (
    <Box sx={{ maxWidth: 1560, mx: 'auto', pb: 5 }}>
      <WelcomeBand
        user={user} me={snap?.me} period={snap?.period}
        school={school} loading={loading} onRefresh={load} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {!snap && loading && (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Skeleton variant="rounded" height={92} />
          <Skeleton variant="rounded" height={340} />
          <Skeleton variant="rounded" height={260} />
        </Box>
      )}

      {snap && (
        <>
          {/* Headline tiles — the school in five numbers, no money */}
          <Box sx={{
            display: 'grid', gap: 1.8, mb: 2.5,
            gridTemplateColumns: {
              xs: '1fr', sm: 'repeat(2, 1fr)',
              lg: `repeat(${reg ? 5 : 4}, 1fr)`,
            },
          }}>
            <Tile icon={<StudentsIcon />} label="Learners on roll"
              value={fmt0(pop?.total)}
              sub={`${fmt0(pop?.male)} boys · ${fmt0(pop?.female)} girls`}
              color={C.blue} />
            <Tile icon={<ClassIcon />} label="Classes running"
              value={fmt0(pop?.classes)}
              sub={`about ${pop?.avg_class_size || 0} learners each`}
              color={C.violet} />
            {reg && (
              <Tile icon={<RegisterIcon />} label="In school today"
                value={reg.marked > 0 ? fmt0(reg.present) : '—'}
                sub={reg.marked > 0
                  ? `${reg.rate}% present · ${fmt0(reg.absent)} absent`
                  : 'register not taken yet'}
                color={C.green} />
            )}
            <Tile icon={<AdmissionsIcon />} label="Admitted this year"
              value={fmt0(pop?.joined_ytd)}
              sub={`${fmt0(pop?.leavers)} have left over time`}
              color={C.teal} />
            <Tile icon={<AlumniIcon />} label="Alumni"
              value={fmt0(pop?.alumni)}
              sub="learners who have completed"
              color={C.amber} />
          </Box>

          {/* The analysis row.
              All three charts share one band so the whole picture of
              the school is readable before anyone scrolls. The
              trajectory takes half again the width of the other two
              because it carries a time axis; on medium screens it
              spans the full width above them, and on phones the row
              simply stacks. Each panel scrolls its own overflow rather
              than stretching the row, so the band keeps a predictable
              height however many classes the school runs. */}
          <Box sx={{
            display: 'grid', gap: 2, mb: 2.5, alignItems: 'stretch',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1.45fr 1fr 1fr' },
          }}>
            <Panel
              icon={<TrendIcon fontSize="small" />}
              title="Population trajectory"
              subtitle={span === 'year' ? 'Learners on roll each year' : 'Learners on roll each month'}
              accent={C.blue}
              sx={{ gridColumn: { xs: 'auto', md: 'span 2', lg: 'auto' } }}
              action={
                <ToggleButtonGroup size="small" exclusive value={span}
                  onChange={(_, v) => v && setSpan(v)}
                  sx={{ '& .MuiToggleButton-root': { px: 1.1, py: .25, fontSize: 11, fontWeight: 700 } }}>
                  <ToggleButton value="year">Year</ToggleButton>
                  <ToggleButton value="month">18 mo</ToggleButton>
                </ToggleButtonGroup>
              }>
              <TrajectoryChart data={trajectory} showRoll height={286} />
              <Stack direction="row" spacing={1.6} sx={{ mt: 1 }} flexWrap="wrap" rowGap={.6}
                alignItems="center">
                <Stack direction="row" spacing={.6} alignItems="center">
                  <Box sx={{ width: 14, height: 3.5, borderRadius: 2, bgcolor: C.blue }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 600 }}>On roll</Typography>
                </Stack>
                <Stack direction="row" spacing={.6} alignItems="center">
                  <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: C.sky }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Admitted</Typography>
                </Stack>
                <Stack direction="row" spacing={.6} alignItems="center">
                  <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: C.amber }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Left</Typography>
                </Stack>
                <Box sx={{ flex: 1 }} />
                {/* The caveat stays on the face of the chart — squeezing
                    the row is no reason to bury how the line was built.
                    The full explanation hangs off the hint. */}
                <Tooltip title={
                  `The roll line starts from today's ${fmt0(pop?.total)} learners and works backwards through recorded admissions and departures.`
                  + (pop?.undated > 0
                      ? ` ${fmt0(pop.undated)} learner${pop.undated === 1 ? ' has' : 's have'} no admission date on file, so they sit at the earliest point shown.`
                      : '')
                }>
                  <Typography sx={{
                    fontSize: 10, color: 'text.secondary', fontStyle: 'italic',
                    cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled',
                  }}>
                    {pop?.undated > 0
                      ? `reconstructed · ${fmt0(pop.undated)} undated`
                      : 'reconstructed from recorded dates'}
                  </Typography>
                </Tooltip>
              </Stack>
            </Panel>

            <ClassPanel rows={snap.classes || []} />
            <ProfilePanel population={pop} ages={snap.ages || []} />
          </Box>

          {/* The day itself */}
          <Box sx={{
            display: 'grid', gap: 2,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: 'repeat(4, 1fr)' },
          }}>
            <AttendancePanel data={reg} />
            <BirthdayPanel rows={snap.birthdays || []} />
            <NewFacesPanel rows={snap.recent_admissions || []} />
            <StaffPanel data={snap.staff} />
          </Box>

        </>
      )}
    </Box>
  );
}
